import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LottoShell from "../components/lotto/LottoShell";
import { useCustomColors } from "../hooks/useCustomColors";
import {
  BetHistoryEntry,
  BetOption,
  BetStatus,
  WalletEntry,
  WalletEntryStatus,
  WalletEntryType,
  getBetHistory,
  getWalletHistory,
  ApiBetHistory,
  ApiWalletHistory,
  toBetHistoryResponse,
  toWalletHistoryResponse,
} from "../api";
import { usePlayerLive } from "../hooks/usePlayerRealtime";

/* Frozen module constants: a fresh [] each render would give every memo and
   effect below a new dependency on every pass. */
const EMPTY_WALLET: WalletEntry[] = [];
const EMPTY_BETS: BetHistoryEntry[] = [];

type HistoryTab = "wallet" | "bets";
type WalletFilter = "ALL" | WalletEntryType;

const HISTORY_TABS: { value: HistoryTab; labelKey: string }[] = [
  { value: "wallet", labelKey: "history_tab_wallet" },
  { value: "bets", labelKey: "history_tab_bets" },
];

const WALLET_FILTERS: { value: WalletFilter; labelKey: string }[] = [
  { value: "ALL", labelKey: "common_all" },
  { value: "TOP_UP", labelKey: "history_wallet_type_top_up" },
  { value: "WITHDRAWAL", labelKey: "history_wallet_type_withdrawal" },
];

/* Every column gets an equal share of the row and the same left alignment, so
   each header sits directly over its values and the gaps between them match. */
const WALLET_COLUMNS = [
  { labelKey: "history_wallet_type", sx: { width: "25%", textAlign: "left" as const } },
  { labelKey: "common_amount", sx: { width: "25%", textAlign: "left" as const } },
  { labelKey: "common_status", sx: { width: "25%", textAlign: "left" as const } },
  { labelKey: "history_wallet_date", sx: { width: "25%", textAlign: "left" as const } },
];

const BET_COLUMNS = [
  { labelKey: "history_wallet_date", sx: { width: "20%", textAlign: "left" as const } },
  { labelKey: "game_bet_slip", sx: { width: "20%", textAlign: "left" as const } },
  { labelKey: "common_stake", sx: { width: "20%", textAlign: "left" as const } },
  { labelKey: "common_payout", sx: { width: "20%", textAlign: "left" as const } },
  { labelKey: "common_status", sx: { width: "20%", textAlign: "left" as const } },
];

const WALLET_TYPE_KEY: Record<WalletEntryType, string> = {
  TOP_UP: "history_wallet_type_top_up",
  WITHDRAWAL: "history_wallet_type_withdrawal",
  BET_STAKE: "history_wallet_type_bet_stake",
  BET_PAYOUT: "history_wallet_type_bet_payout",
  BET_REFUND: "history_wallet_type_bet_refund",
};

const WALLET_STATUS_KEY: Record<WalletEntryStatus, string> = {
  PENDING: "status_pending",
  PROCESSING: "status_processing",
  SUCCESS: "status_success",
  FAILED: "status_failed",
  REJECTED: "status_rejected",
};

const BET_STATUS_KEY: Record<BetStatus, string> = {
  PENDING: "status_pending",
  WON: "status_won",
  LOST: "status_lost",
  VOID: "status_void",
};

const BET_PLAY_KEY: Record<BetOption, string> = {
  BIG: "game_bet_big",
  SMALL: "game_bet_small",
  ODD: "game_bet_odd",
  EVEN: "game_bet_even",
};

const PAGE_LIMIT = 10;

// const DEFAULT_CURRENCY = "MYR";

/** The trophy the reveal popup uses, so a won bet reads the same way here. */
const WIN_CUP = "/assets/win-coin.webp";

const formatAmount = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const HistoryPage = () => {
  const { t } = useTranslation();
  const colors = useCustomColors();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: HistoryTab =
    searchParams.get("tab") === "bets" ? "bets" : "wallet";
  const [walletFilter, setWalletFilter] = useState<WalletFilter>("ALL");
  const [page, setPage] = useState(1);

  const setTab = (value: HistoryTab) => {
    setSearchParams({ tab: value });
    setPage(1);
  };

  /* One query per tab, keyed by what it actually depends on. The inactive tab
     is disabled rather than fetched and thrown away, and because the key is the
     identity of the request, StrictMode's second mount and any re-render reuse
     the in-flight promise instead of starting another. */
  const walletQuery = useQuery({
    queryKey: ["history", "wallet", walletFilter, page],
    queryFn: () =>
      getWalletHistory({
        type: walletFilter === "ALL" ? undefined : walletFilter,
        page,
        limit: PAGE_LIMIT,
      }),
    enabled: tab === "wallet",
  });

  const betsQuery = useQuery({
    queryKey: ["history", "bets", page],
    queryFn: () => getBetHistory({ page, limit: PAGE_LIMIT }),
    enabled: tab === "bets",
  });

  const activeQuery = tab === "wallet" ? walletQuery : betsQuery;
  const isLoading = activeQuery.isPending;

  const walletResponse = walletQuery.data;
  const wallet = walletResponse?.status === 1 ? walletResponse.data : EMPTY_WALLET;
  const bets = betsQuery.data?.status === 1 ? betsQuery.data.data : EMPTY_BETS;
  const pagination =
    (tab === "wallet" ? walletResponse?.pagination : betsQuery.data?.pagination) ??
    null;

  useEffect(() => {
    if (activeQuery.error) {
      console.error("Load history error:", activeQuery.error);
    }
  }, [activeQuery.error]);

  const queryClient = useQueryClient();

  const walletLiveParams = {
    type: walletFilter === "ALL" ? "all" : walletFilter.toLowerCase(),
    page,
    perPage: PAGE_LIMIT,
  };
  usePlayerLive<ApiWalletHistory>(
    "my-transactions",
    walletLiveParams,
    (response) => {
      queryClient.setQueryData(
        ["history", "wallet", walletFilter, page],
        toWalletHistoryResponse(response)
      );
    },
    { enabled: tab === "wallet" }
  );

  usePlayerLive<ApiBetHistory>(
    "my-bets",
    { page, perPage: PAGE_LIMIT },
    (response) => {
      queryClient.setQueryData(["history", "bets", page], toBetHistoryResponse(response));
    },
    { enabled: tab === "bets" }
  );

  // const summary = useMemo(() => {
  //   if (tab === "wallet") {
  //     return [
  //       {
  //         labelKey: "history_wallet_total_top_up",
  //         value: walletSummary?.totalTopUp ?? 0,
  //         currency: DEFAULT_CURRENCY,
  //       },
  //       {
  //         labelKey: "history_wallet_total_withdrawal",
  //         value: walletSummary?.totalWithdrawal ?? 0,
  //         currency: DEFAULT_CURRENCY,
  //         tint: colors.accent.cyan,
  //       },
  //     ];
  //   }
  //   return [
  //     {
  //       labelKey: "history_bets_total_staked",
  //       value: bets.reduce((sum, bet) => sum + bet.total_stake, 0),
  //       currency: DEFAULT_CURRENCY,
  //       tint: colors.accent.green,
  //     },
  //     {
  //       labelKey: "history_bets_total_payout",
  //       value: bets.reduce((sum, bet) => sum + bet.payout, 0),
  //       currency: DEFAULT_CURRENCY,
  //     },
  //   ];
  // }, [tab, walletSummary, bets, colors]);

  const describePlay = (bet: BetHistoryEntry) =>
    bet.selections
      .map((selection) =>
        t("history_bets_play_value", {
          position: selection.position,
          play: t(BET_PLAY_KEY[selection.option]),
        })
      )
      .join(" · ");

  const walletStatusTint = (status: WalletEntryStatus) => {
    if (status === "SUCCESS") return colors.accent.green;
    if (status === "FAILED" || status === "REJECTED") return colors.accent.danger;
    return colors.accent.cyan;
  };

  const betStatusTint = (status: BetStatus) => {
    if (status === "WON") return colors.accent.green;
    if (status === "LOST") return colors.accent.danger;
    if (status === "PENDING") return colors.accent.amber;
    if (status === "VOID") return colors.accent.cyan;
    return colors.text.disabled;
  };

  const eyebrowSx = {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "3px",
    color: colors.text.secondary,
  };

  const headCellSx = {
    py: "14px",
    px: "24px",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "1.5px",
    textTransform: "uppercase" as const,
    color: colors.table.headText,
    background: colors.table.headBg,
    borderTop: `1px solid ${colors.table.headDivider}`,
  };

  const bodyCellSx = {
    py: "18px",
    px: "24px",
    fontSize: "13px",
    fontVariantNumeric: "tabular-nums",
    color: colors.text.primary,
  };

  /* A won bet is told by the cup alone — no pill, no word. The label moves to
     `alt`, since with the text gone the image is what has to carry it. */
  const badge = (label: string, tint: string, icon?: string) =>
    icon ? (
      <Box
        component="img"
        src={icon}
        alt={label}
        sx={{ width: "26px", height: "26px", objectFit: "contain", verticalAlign: "middle" }}
      />
    ) : (
      <Box
        sx={{
          height: "24px",
          px: "12px",
          display: "inline-flex",
          alignItems: "center",
          borderRadius: "12px",
          fontSize: "11px",
          fontWeight: 700,
          color: tint,
          backgroundColor: `${tint}29`,
          border: `1px solid ${tint}3D`,
        }}
      >
        {label}
      </Box>
    );

  const pillSx = (isActive: boolean) => ({
    height: "35px",
    px: "22px",
    display: "flex",
    alignItems: "center",
    borderRadius: "18px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    color: isActive ? "#FFFFFF" : colors.text.secondary,
    background: isActive ? colors.gradient.brand : colors.isDark ? "rgba(17, 16, 16, 0.86)" : colors.background.panelSecondary,
    border: `1px solid ${isActive ? "transparent" : colors.border.accent}`,
    boxShadow: isActive ? colors.gradient.brandGlow : "none",
    transition: "color 150ms ease, border-color 150ms ease",
    "&:hover": isActive
      ? undefined
      : { color: colors.text.primary, borderColor: colors.border.strong },
  });

  const rowBorder = `1px solid ${colors.table.rowDivider}`;

  const emptyState = (messageKey: string) => (
    <Box sx={{ py: "40px", textAlign: "center" }}>
      <Typography sx={{ fontSize: "14px", color: colors.text.disabled }}>
        {t(messageKey)}
      </Typography>
    </Box>
  );

  return (
    <LottoShell>
      {/* History tabs */}
      <Box
        sx={{
          mt: { xs: 0, sm: "23px" },
          p: "4px",
          display: "inline-flex",
          gap: "4px",
          borderRadius: "22px",
          backgroundColor: colors.background.rail,
          border: `1px solid ${colors.card.border}`,
        }}
      >
        {HISTORY_TABS.map((item) => (
          <Box
            key={item.value}
            onClick={() => setTab(item.value)}
            sx={pillSx(tab === item.value)}
          >
            {t(item.labelKey)}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          mt: { xs: "20px", sm: "22px" },
          borderRadius: "20px",
          background: colors.card.gradient,
          border: `1px solid ${colors.card.border}`,
          boxShadow: colors.card.shadow,
          overflow: "hidden",
        }}
      >
        {/* Panel header + summary */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          sx={{
            p: { xs: "20px", md: "28px" },
            gap: "18px",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "20px",
                fontWeight: 800,
                fontStyle: "italic",
                color: colors.accent.gold,
              }}
            >
              {t(`history_tab_${tab}`)}
            </Typography>
            <Box
              sx={{
                mt: "8px",
                width: "64px",
                height: "3px",
                borderRadius: "2px",
                background: colors.gradient.divider,
              }}
            />
            <Typography
              sx={{ ...eyebrowSx, mt: "4px", textTransform: "uppercase" }}
            >
              {t(`history_tab_${tab}`)}
            </Typography>
          </Box>
          {/* <Stack
            direction="row"
            flexWrap="wrap"
            sx={{
              gap: { xs: "20px", md: "32px" },
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            {summary.map((item) => (
              <Box key={item.labelKey}>
                <Typography sx={{ ...eyebrowSx, letterSpacing: "2px" }}>
                  {t(item.labelKey)}
                </Typography>
                <Typography
                  sx={{
                    mt: "4px",
                    fontSize: "22px",
                    fontWeight: 900,
                    whiteSpace: "nowrap",
                    ...(item.tint
                      ? { color: item.tint }
                      : goldShineSx(colors)),
                  }}
                >
                  {item.currency} {formatAmount(item.value)}
                </Typography>
              </Box>
            ))}
          </Stack> */}
        </Stack>

        {/* Wallet type filter */}
        {tab === "wallet" && (
          <Stack
            direction="row"
            flexWrap="wrap"
            sx={{ px: { xs: "20px", md: "28px" }, pb: "20px", gap: "8px" }}
          >
            {WALLET_FILTERS.map((filter) => (
              <Box
                key={filter.value}
                onClick={() => {
                  setWalletFilter(filter.value);
                  setPage(1);
                }}
                sx={{
                  ...pillSx(walletFilter === filter.value),
                  height: "30px",
                  px: "16px",
                  fontSize: "12px",
                  border: `1px solid ${
                    walletFilter === filter.value
                      ? "transparent"
                      : colors.border.accent
                  }`,
                }}
              >
                {t(filter.labelKey)}
              </Box>
            ))}
          </Stack>
        )}

        {isLoading ? (
          <Box sx={{ px: { xs: "20px", md: "28px" }, pb: "28px" }}>
            {[0, 1, 2, 3].map((row) => (
              <Skeleton
                key={row}
                variant="rounded"
                height={56}
                sx={{ mb: "10px", bgcolor: colors.background.panelSecondary }}
              />
            ))}
          </Box>
        ) : (
          <>
            {/* ---------- Desktop table ---------- */}
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                px: "28px",
                pb: "8px",
              }}
            >
              {tab === "wallet" ? (
                wallet.length === 0 ? (
                  emptyState("history_wallet_empty")
                ) : (
                  <Box
                    component="table"
                    sx={{ width: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0, background: colors.table.surface, border: `1px solid ${colors.table.border}`, borderRadius: "16px", overflow: "hidden", boxShadow: colors.table.frame }}
                  >
                    <Box component="thead">
                      <Box component="tr" sx={{ borderBottom: rowBorder }}>
                        {WALLET_COLUMNS.map((column) => (
                          <Box
                            component="th"
                            key={column.labelKey}
                            sx={{ ...headCellSx, ...column.sx }}
                          >
                            {t(column.labelKey)}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {wallet.map((entry, rowIndex) => (
                        <Box
                          component="tr"
                          key={rowIndex}
                          sx={{
                            backgroundColor: rowIndex % 2 === 0 ? colors.table.rowOdd : colors.table.rowEven,
                            borderBottom: rowBorder,
                            transition: "background-color 150ms ease",
                            "&:first-of-type td:first-of-type": { pt: "24px" },
                            "&:hover": {
                              background: colors.table.rowHover,
                            },
                          }}
                        >
                          <Box component="td" sx={{ ...bodyCellSx, ...WALLET_COLUMNS[0].sx }}>
                            {t(WALLET_TYPE_KEY[entry.type])}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              ...bodyCellSx,
                              ...WALLET_COLUMNS[1].sx,
                              whiteSpace: "nowrap",
                              fontWeight: 700,
                              color:
                                entry.amount >= 0
                                  ? colors.accent.green
                                  : colors.text.primary,
                            }}
                          >
                            {entry.amount >= 0 ? "+" : "−"}{" "}
                            {entry.currency} {formatAmount(Math.abs(entry.amount))}
                          </Box>
                          <Box component="td" sx={{ ...bodyCellSx, ...WALLET_COLUMNS[2].sx }}>
                            {badge(
                              t(WALLET_STATUS_KEY[entry.status]),
                              walletStatusTint(entry.status)
                            )}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              ...bodyCellSx,
                              ...WALLET_COLUMNS[3].sx,
                              whiteSpace: "nowrap",
                              color: colors.text.secondary,
                            }}
                          >
                            {formatDateTime(entry.created_at)}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )
              ) : bets.length === 0 ? (
                emptyState("history_bets_empty")
              ) : (
                <Box
                  component="table"
                  sx={{ width: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0, background: colors.table.surface, border: `1px solid ${colors.table.border}`, borderRadius: "16px", overflow: "hidden", boxShadow: colors.table.frame }}
                >
                  <Box component="thead">
                    <Box component="tr" sx={{ borderBottom: rowBorder }}>
                      {BET_COLUMNS.map((column) => (
                        <Box
                          component="th"
                          key={column.labelKey}
                          sx={{ ...headCellSx, ...column.sx }}
                        >
                          {t(column.labelKey)}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {bets.map((bet, rowIndex) => (
                      <Box
                        component="tr"
                        key={rowIndex}
                        sx={{
                          backgroundColor: rowIndex % 2 === 0 ? colors.table.rowOdd : colors.table.rowEven,
                          borderBottom: rowBorder,
                          transition: "background-color 150ms ease",
                          "&:first-of-type td:first-of-type": { pt: "24px" },
                          "&:hover": {
                            background: colors.table.rowHover,
                          },
                        }}
                      >
                        <Box
                          component="td"
                          sx={{
                            ...bodyCellSx,
                            ...BET_COLUMNS[0].sx,
                            whiteSpace: "nowrap",
                            color: colors.text.secondary,
                          }}
                        >
                          {formatDateTime(bet.created_at)}
                        </Box>
                        <Box component="td" sx={{ ...bodyCellSx, ...BET_COLUMNS[1].sx }}>
                          {describePlay(bet)}
                        </Box>
                        <Box
                          component="td"
                          sx={{ ...bodyCellSx, ...BET_COLUMNS[2].sx, whiteSpace: "nowrap" }}
                        >
                          {formatAmount(bet.total_stake)}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            ...bodyCellSx,
                            ...BET_COLUMNS[3].sx,
                            whiteSpace: "nowrap",
                            fontWeight: 700,
                            color:
                              bet.payout > 0
                                ? colors.accent.green
                                : colors.text.secondary,
                          }}
                        >
                          {bet.status === "PENDING"
                            ? "—"
                            : formatAmount(bet.payout)}
                        </Box>
                        <Box component="td" sx={{ ...bodyCellSx, ...BET_COLUMNS[4].sx }}>
                          {badge(
                            t(BET_STATUS_KEY[bet.status]),
                            betStatusTint(bet.status),
                            bet.status === "WON" ? WIN_CUP : undefined
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* ---------- Mobile cards ---------- */}
            <Stack
              sx={{
                display: { xs: "flex", md: "none" },
                px: "20px",
                pb: "8px",
                gap: "10px",
              }}
            >
              {tab === "wallet"
                ? wallet.length === 0
                  ? emptyState("history_wallet_empty")
                  : wallet.map((entry, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: "16px",
                          borderRadius: "16px",
                          backgroundColor: colors.background.panelSecondary,
                          border: `1px solid ${colors.border.accent}`,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ gap: "10px" }}
                        >
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: colors.text.primary,
                            }}
                          >
                            {t(WALLET_TYPE_KEY[entry.type])}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 800,
                              color:
                              entry.amount >= 0
                                ? colors.accent.green
                                : colors.text.primary,
                            }}
                          >
                            {entry.amount >= 0 ? "+" : "−"}{" "}
                            {entry.currency} {formatAmount(Math.abs(entry.amount))}
                          </Typography>
                        </Stack>
                        <Typography
                          sx={{
                            mt: "8px",
                            fontSize: "11.5px",
                            color: colors.text.secondary,
                          }}
                        >
                          {formatDateTime(entry.created_at)}
                        </Typography>
                        <Box sx={{ mt: "12px" }}>
                          {badge(
                            t(WALLET_STATUS_KEY[entry.status]),
                            walletStatusTint(entry.status)
                          )}
                        </Box>
                      </Box>
                    ))
                : bets.length === 0
                ? emptyState("history_bets_empty")
                : bets.map((bet, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: "16px",
                        borderRadius: "16px",
                        backgroundColor: colors.background.panelSecondary,
                        border: `1px solid ${colors.border.accent}`,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ gap: "10px" }}
                      >
                        <Typography
                          sx={{
                            fontSize: "11.5px",
                            color: colors.text.secondary,
                          }}
                        >
                          {formatDateTime(bet.created_at)}
                        </Typography>
                        {badge(
                          t(BET_STATUS_KEY[bet.status]),
                          betStatusTint(bet.status),
                          bet.status === "WON" ? WIN_CUP : undefined
                        )}
                      </Stack>
                      <Typography
                        sx={{
                          mt: "8px",
                          fontSize: "12px",
                          color: colors.text.secondary,
                        }}
                      >
                        {describePlay(bet)}
                      </Typography>
                      <Stack direction="row" sx={{ mt: "14px", gap: "10px" }}>
                        {[
                          {
                            labelKey: "common_stake",
                            value: formatAmount(bet.total_stake),
                          },
                          {
                            labelKey: "common_payout",
                            value:
                              bet.status === "PENDING"
                                ? "—"
                                : formatAmount(bet.payout),
                            tint:
                              bet.payout > 0
                                ? colors.accent.green
                                : colors.text.secondary,
                          },
                        ].map((cell) => (
                          <Box key={cell.labelKey} sx={{ flex: 1 }}>
                            <Typography
                              sx={{ ...eyebrowSx, letterSpacing: "2px" }}
                            >
                              {t(cell.labelKey)}
                            </Typography>
                            <Typography
                              sx={{
                                mt: "4px",
                                fontSize: "14px",
                                fontWeight: 700,
                                color: cell.tint ?? colors.text.primary,
                              }}
                            >
                              {cell.value}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ))}
            </Stack>
          </>
        )}

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              p: { xs: "20px", md: "24px" },
              gap: "10px",
              borderTop: rowBorder,
            }}
          >
            <Button
              variant="outlinedDark"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
              sx={{ height: "38px", borderRadius: "10px", px: "18px" }}
            >
              {t("common_previous")}
            </Button>
            <Typography
              sx={{ fontSize: "12.5px", color: colors.text.secondary }}
            >
              {page} / {pagination.total_pages}
            </Typography>
            <Button
              variant="outlinedDark"
              disabled={page >= pagination.total_pages}
              onClick={() => setPage((current) => current + 1)}
              sx={{ height: "38px", borderRadius: "10px", px: "18px" }}
            >
              {t("common_next")}
            </Button>
          </Stack>
        )}
      </Box>
    </LottoShell>
  );
};

export default HistoryPage;
